import { Controller } from "@hotwired/stimulus"

/**
 * Flash message controller with auto-dismiss and manual close
 *
 * Usage:
 * <div data-controller="flash"
 *      data-flash-dismiss-value="5000"
 *      class="flash-message">
 *   <span>Your message was sent!</span>
 *   <button data-action="click->flash#dismiss" aria-label="Dismiss">
 *     <svg>...</svg>
 *   </button>
 * </div>
 */
export default class extends Controller {
  static values = {
    dismiss: { type: Number, default: 5000 },
    autoDismiss: { type: Boolean, default: true }
  }

  connect() {
    this.element.classList.add("flash-enter")

    // Trigger reflow to ensure animation plays
    void this.element.offsetHeight

    this.element.classList.remove("flash-enter")
    this.element.classList.add("flash-enter-active")

    if (this.autoDismissValue && this.dismissValue > 0) {
      this.timeout = setTimeout(() => {
        this.dismiss()
      }, this.dismissValue)
    }
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout)
    }
  }

  dismiss(event) {
    if (event) event.preventDefault()

    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // Add fade out animation
    this.element.classList.add("flash-leave")
    this.element.classList.remove("flash-enter-active")

    // Trigger reflow
    void this.element.offsetHeight

    this.element.classList.add("flash-leave-active")

    // Remove element after animation completes
    const animationDuration = this.getAnimationDuration()
    this.leaveTimeout = setTimeout(() => {
      this.element.remove()
      this.dispatch("dismissed")
    }, animationDuration)
  }

  getAnimationDuration() {
    const style = getComputedStyle(this.element)
    const duration = style.transitionDuration || style.animationDuration || "0.3s"
    const value = parseFloat(duration)
    // Handle both milliseconds (300ms) and seconds (0.3s) formats
    return duration.includes("ms") ? value : value * 1000
  }
}
